/**
 * Published output for a block document.
 *
 * This component is the site's single renderer. The public page mounts it, and
 * the editor's preview mounts the *same* component with the *same* props, so
 * "preview" is not an approximation of the published page — it is the published
 * page. A second renderer, however faithful on the day it is written, drifts
 * within a month and quietly starts lying to editors.
 *
 * Two habits keep the public page tidy. Blocks are normalised first, so a
 * record saved before a block type existed cannot crash a visitor's page; and
 * empty blocks are skipped, so a template slot the author never filled leaves
 * no hole in the layout.
 *
 * Layer rule: element choice and semantic class names only. Not one colour,
 * size or font lives here.
 *
 * 块文档的公开渲染结果。
 *
 * 这是站点唯一的渲染器。公开页挂载它，编辑器预览挂载**同一个**组件、传**同样**的
 * 属性，所以「预览」不是对公开页的近似——它就是公开页。第二个渲染器无论写成当天
 * 多么忠实，一个月内必然漂移，然后开始悄悄欺骗编辑。
 *
 * 两个习惯让公开页保持整洁：先做 normalise，早于某个块类型的历史记录不会让访客
 * 页面崩溃；再跳过空块，作者没填的模板槽不会在版面上留下一个洞。
 *
 * 分层规则：只决定元素与语义类名。这里没有任何颜色、尺寸或字体。
 */

import { useMemo } from 'react'
import { isBlockEmpty, normaliseBlocks } from '../lib/blocks.js'
import { isSafeUrl } from '../lib/inline.js'
import { toEmbedUrl } from '../lib/video-embed.js'
import { useI18n } from '../lib/i18n.jsx'
import { cx, Hairline, Media } from '../ui/index.js'
import { InlineText } from './InlineText.jsx'

/**
 * Video hosts whose player may be framed on this site.
 *
 * An `<iframe src>` taken straight from author input is a script-injection
 * hole: whatever that page loads runs in a frame on our origin's tab, can
 * navigate the top window, and inherits our users' trust in the URL bar. So the
 * host is checked against this list and anything else degrades to a plain link.
 *
 * 允许在本站内嵌播放器的视频站点。
 *
 * 直接把作者输入塞进 <iframe src> 是一个脚本注入口子：那个页面加载的任何东西都在
 * 我们这个标签页里跑，可以导航顶层窗口，还继承了用户对地址栏的信任。因此必须校验
 * 域名，白名单之外一律降级为普通链接。
 */

/**
 * Read the URL out of an upload value.
 *
 * Upload fields store a URL string, but a record written by an older build (or
 * imported by hand) may hold `{url, name}`. Both are accepted here rather than
 * in the schema, because a visitor's page must never be the place a legacy
 * shape surfaces.
 *
 * These few lines are deliberately duplicated in the upload fields instead of
 * shared: the public renderer must not import the editor's upload code, or
 * every visitor downloads the console's uploader.
 *
 * 从上传值里取出 URL。上传字段存的是 URL 字符串，但更早版本写入（或手工导入）的
 * 记录可能是 {url, name}。两种都在这里接受，而不是在 schema 里——访客页面绝不该
 * 是历史格式暴露出来的地方。
 *
 * 这几行是刻意在上传字段里重复实现而非共享的：公开渲染器不能引入编辑器的上传
 * 代码，否则每个访客都会下载后台的上传器。
 *
 * @param {string|{url: string}} value Upload field value / 上传字段值。
 * @returns {string} URL, or an empty string / URL，没有则为空串。
 */
function assetUrl(value) {
  if (!value) return ''
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'object' && typeof value.url === 'string') return value.url.trim()
  return ''
}

/**
 * Best available display name for an upload value.
 *
 * 上传值可用的最佳显示名称。
 *
 * @param {string|{url: string, name?: string}} value Upload field value / 上传字段值。
 * @returns {string} File name / 文件名。
 */
function assetName(value) {
  if (value && typeof value === 'object' && value.name) return String(value.name)
  const url = assetUrl(value)
  if (!url) return ''
  const tail = url.split('/').pop() || url
  try {
    return decodeURIComponent(tail)
  } catch {
    return tail
  }
}

/**
 * Heading block.
 *
 * 小标题块。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.data Block data / 块数据。
 * @returns {JSX.Element|null} Heading / 标题。
 */
function HeadingBlock({ data }) {
  const { pick } = useI18n()
  const text = String(pick(data.text) || '').trim()
  if (!text) return null
  const level = Number(data.level) === 3 ? 3 : 2
  const Tag = level === 3 ? 'h3' : 'h2'
  return <Tag className={cx('block', 'block--heading', `block--heading-h${level}`)}>{text}</Tag>
}

/**
 * Paragraph block.
 *
 * 正文段落块。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.data Block data / 块数据。
 * @returns {JSX.Element|null} Paragraph / 段落。
 */
function ParagraphBlock({ data }) {
  const { pick } = useI18n()
  const text = String(pick(data.text) || '')
  if (!text.trim()) return null
  return (
    <p className="block block--paragraph">
      <InlineText text={text} />
    </p>
  )
}

/**
 * List block, ordered or unordered.
 *
 * 列表块，有序或无序。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.data Block data / 块数据。
 * @returns {JSX.Element|null} List / 列表。
 */
function ListBlock({ data }) {
  const { pick } = useI18n()
  const raw = pick(data.items)
  const items = (Array.isArray(raw) ? raw : String(raw || '').split(/\r?\n/))
    .map((line) => String(line).trim())
    .filter(Boolean)
  if (!items.length) return null

  const ordered = !!data.ordered
  const Tag = ordered ? 'ol' : 'ul'
  return (
    <Tag className={cx('block', 'block--list', ordered && 'block--list-ordered')}>
      {items.map((item, index) => (
        <li className="block__list-item" key={`${index}-${item}`}>
          <InlineText text={item} />
        </li>
      ))}
    </Tag>
  )
}

/**
 * Image block with an optional caption.
 *
 * 图片块，可带图注。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.data Block data / 块数据。
 * @returns {JSX.Element|null} Figure / 图片。
 */
function ImageBlock({ data }) {
  const { pick } = useI18n()
  const src = assetUrl(data.src)
  if (!src) return null
  const caption = String(pick(data.caption) || '').trim()
  // Only the three declared widths are honoured; anything else falls back to
  // text width so a hand-edited record cannot invent a layout.
  // 只认 schema 声明的三种宽度，其余一律回退到正文宽度，
  // 手工改过的记录不能凭空造出一种版式。
  const width = ['text', 'wide', 'full'].includes(data.width) ? data.width : 'text'

  return (
    <figure className={cx('block', 'block--image', `block--image-${width}`, 'block__figure')}>
      <img
        className="block__image"
        src={src}
        alt={caption || assetName(data.src)}
        loading="lazy"
        decoding="async"
      />
      {caption && <figcaption className="block__caption">{caption}</figcaption>}
    </figure>
  )
}

/**
 * Gallery block — several images on a grid.
 *
 * 图片组块 —— 栅格排列的多张图片。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.data Block data / 块数据。
 * @returns {JSX.Element|null} Figure / 图片组。
 */
function GalleryBlock({ data }) {
  const { pick } = useI18n()
  const items = (Array.isArray(data.items) ? data.items : []).map(assetUrl).filter(Boolean)
  if (!items.length) return null
  const caption = String(pick(data.caption) || '').trim()
  // The column count is data, not design, so it travels as a custom property
  // and the theme still decides gaps, rounding and breakpoint behaviour.
  // 列数属于数据而非设计，因此以 CSS 变量传入，间距、圆角和断点行为仍由主题决定。
  const columns = [2, 3, 4].includes(Number(data.columns)) ? Number(data.columns) : 3

  return (
    <figure className={cx('block', 'block--gallery', 'block__figure')}>
      <ul className="block__gallery" style={{ '--gallery-columns': columns }}>
        {items.map((url, index) => (
          <li className="block__gallery-item" key={`${index}-${url}`}>
            <img className="block__gallery-image" src={url} alt="" loading="lazy" decoding="async" />
          </li>
        ))}
      </ul>
      {caption && <figcaption className="block__caption">{caption}</figcaption>}
    </figure>
  )
}

/**
 * Video block — an uploaded file, an allow-listed embed, or a link.
 *
 * 视频块 —— 上传的文件、白名单内的嵌入播放器，或者一个链接。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.data Block data / 块数据。
 * @returns {JSX.Element|null} Figure / 视频。
 */
function VideoBlock({ data }) {
  const { pick } = useI18n()
  const src = assetUrl(data.src)
  const poster = assetUrl(data.poster)
  const url = String(data.url || '').trim()
  // The pasted address is almost never the embeddable one; when it cannot be
  // converted this is '' and the link fallback below runs, which is a thing the
  // visitor can click rather than a blank frame.
  // 粘贴进来的地址几乎从来不是可嵌入的那个；转换不出来时这里是空串，
  // 于是走下面的链接兜底——那是访客点得动的东西，而不是一块空白。
  const embedUrl = toEmbedUrl(url)
  const caption = String(pick(data.caption) || '').trim()

  let body = null
  if (src) {
    body = (
      <video
        className="block__video"
        src={src}
        poster={poster || undefined}
        controls
        preload="metadata"
      />
    )
  } else if (embedUrl) {
    body = (
      <Media ratio="16 / 9" className="block__embed">
        <iframe
          className="block__embed-frame"
          src={embedUrl}
          title={caption || url}
          loading="lazy"
          allow="accelerometer; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </Media>
    )
  } else if (url && isSafeUrl(url)) {
    body = (
      <a className="ui-link block__video-link" href={url} target="_blank" rel="noopener noreferrer">
        {url}
      </a>
    )
  }
  if (!body) return null

  return (
    <figure className={cx('block', 'block--video', 'block__figure')}>
      {body}
      {caption && <figcaption className="block__caption">{caption}</figcaption>}
    </figure>
  )
}

/**
 * Audio block.
 *
 * 音频块。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.data Block data / 块数据。
 * @returns {JSX.Element|null} Figure / 音频。
 */
function AudioBlock({ data }) {
  const { pick } = useI18n()
  const src = assetUrl(data.src)
  if (!src) return null
  const title = String(pick(data.title) || '').trim()
  const caption = String(pick(data.caption) || '').trim()

  return (
    <figure className={cx('block', 'block--audio', 'block__figure')}>
      {title && <p className="block__audio-title">{title}</p>}
      <audio className="block__audio" src={src} controls preload="metadata" />
      {caption && <figcaption className="block__caption">{caption}</figcaption>}
    </figure>
  )
}

/**
 * Attachment block — a download link.
 *
 * 附件下载块。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.data Block data / 块数据。
 * @returns {JSX.Element|null} Download link / 下载链接。
 */
function AttachmentBlock({ data }) {
  const { pick, t } = useI18n()
  const src = assetUrl(data.src)
  if (!src) return null
  const label = String(pick(data.label) || '').trim() || assetName(data.src) || t('common.download')

  return (
    <p className="block block--attachment">
      <a
        className="ui-link block__attachment"
        href={src}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="block__attachment-glyph" aria-hidden="true">
          ⇩
        </span>
        <span className="block__attachment-label">{label}</span>
      </a>
    </p>
  )
}

/**
 * Quote block.
 *
 * 引用块。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.data Block data / 块数据。
 * @returns {JSX.Element|null} Blockquote / 引用。
 */
function QuoteBlock({ data }) {
  const { pick } = useI18n()
  const text = String(pick(data.text) || '')
  if (!text.trim()) return null
  const cite = String(pick(data.cite) || '').trim()

  return (
    <blockquote className="block block--quote">
      <p className="block__quote-text">
        <InlineText text={text} />
      </p>
      {cite && <footer className="block__quote-cite">{cite}</footer>}
    </blockquote>
  )
}

/**
 * Code block.
 *
 * The language is exposed as a data attribute and nothing more. No syntax
 * highlighter ships with this site: it would cost every visitor a large
 * dependency to colour the handful of snippets a research page carries, and
 * highlighted code in one palette rarely survives a theme switch.
 *
 * 代码块。语言只作为 data 属性暴露，仅此而已。站点不带语法高亮：为了给研究页面
 * 上寥寥几段代码上色，让每个访客付出一个大依赖并不划算，而且按某套配色高亮的代码
 * 很难在换主题后依然成立。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.data Block data / 块数据。
 * @returns {JSX.Element|null} Preformatted code / 预格式化代码。
 */
function CodeBlock({ data }) {
  const text = String(data.text || '')
  if (!text.trim()) return null
  const language = String(data.language || '').trim()

  return (
    <pre className="block block--code" data-language={language || undefined}>
      <code className="block__code">{text}</code>
    </pre>
  )
}

/**
 * Divider block.
 *
 * 分隔线块。
 *
 * @returns {JSX.Element} Hairline / 发丝线。
 */
function DividerBlock() {
  return <Hairline className="block block--divider" />
}

/**
 * One render function per block type. An unknown key renders nothing.
 *
 * 每种块类型一个渲染函数。未知键名不渲染任何东西。
 */
const RENDERERS = {
  heading: HeadingBlock,
  paragraph: ParagraphBlock,
  list: ListBlock,
  image: ImageBlock,
  gallery: GalleryBlock,
  video: VideoBlock,
  audio: AudioBlock,
  attachment: AttachmentBlock,
  quote: QuoteBlock,
  code: CodeBlock,
  divider: DividerBlock,
}

/**
 * Render a block array as published output.
 *
 * 把块数组渲染为公开发布的内容。
 *
 * @param {object} props Props / 属性。
 * @param {Array<{id: string, type: string, data: object}>} props.blocks Block array / 块数组。
 * @param {string} [props.className] Extra class from the caller / 调用方附加的类名。
 * @returns {JSX.Element|null} Rendered document, or null when there is nothing / 渲染结果，无内容则为 null。
 */
export function BlockRenderer({ blocks, className }) {
  const visible = useMemo(
    () => normaliseBlocks(blocks).filter((block) => !isBlockEmpty(block)),
    [blocks]
  )
  if (!visible.length) return null

  return (
    <div className={cx('blocks', className)}>
      {visible.map((block) => {
        // normaliseBlocks has already dropped unknown types; this guard is here
        // so a future caller that skips normalisation still cannot crash a page.
        // normaliseBlocks 已经丢弃了未知类型；这道保险是留给将来「跳过 normalise」
        // 的调用方的——即便如此也不能让页面崩溃。
        const Renderer = RENDERERS[block.type]
        if (!Renderer) return null
        return <Renderer key={block.id} data={block.data} />
      })}
    </div>
  )
}

export default BlockRenderer
